/**
 * Competition rules and the numbers derived from a climber's ascents, shared by the app (lib/insights.ts)
 * and the server (social stats, boards): the numbers you see on your own profile and the ones your
 * friends see come from these functions. Pure and timezone-free: dates are the climber's local
 * `YYYY-MM-DD`, read with `Date.UTC`, never with the process time zone. See docs/archive/PLAN-SOCIAL.md,
 * "Competition rules".
 */
import {
  GAIN_CAP_M,
  GRACE_DAYS,
  HUELLAS_LOGRO,
  PAUSE_MAX_PER_YEAR,
  PAUSE_MAX_RUN,
  PUBLISH_HOUR_UTC,
  REFUGIO_EVERY,
  REFUGIO_MAX,
  STREAK_LOGROS,
  SUMMIT_LOGROS,
} from "./social";

/** Everest, for "Entre todos, 1,4 Everest este mes" and "you have climbed N Everests". */
export const EVEREST_M = 8849;
export const TRESMIL_M = 3000;

const DAY_MS = 86_400_000;

// ── Days ───────────────────────────────────────────────────────────────────────────────────────────

/** Days since 1970-01-01 of a `YYYY-MM-DD` date. */
export function dayNumber(date: string): number {
  return Math.round(Date.UTC(Number(date.slice(0, 4)), Number(date.slice(5, 7)) - 1, Number(date.slice(8, 10))) / DAY_MS);
}

export const dateOfDay = (day: number) => new Date(day * DAY_MS).toISOString().slice(0, 10);

export const addDays = (date: string, n: number) => dateOfDay(dayNumber(date) + n);

/** Today's date in UTC (the server's notion of today; the client sends its own local one). */
export const utcToday = (now: number = Date.now()) => new Date(now).toISOString().slice(0, 10);

export const isDate = (s: string) => /^\d{4}-\d{2}-\d{2}$/.test(s) && dateOfDay(dayNumber(s)) === s;

// ── ISO weeks (Monday to Sunday) ───────────────────────────────────────────────────────────────────

/** Weeks since the one holding 1970-01-01 (a Thursday): Monday-based, so consecutive weeks differ by 1. */
export const weekIndex = (date: string) => Math.floor((dayNumber(date) + 3) / 7);

export const weekMonday = (index: number) => dateOfDay(index * 7 - 3);
const weekThursday = (index: number) => dateOfDay(index * 7);

/** "2026-W53": the ISO week-year (the year of the week's Thursday) and number. */
export function weekLabel(index: number): string {
  const thursday = weekThursday(index);
  const year = Number(thursday.slice(0, 4));
  const n = Math.floor((dayNumber(thursday) - dayNumber(`${thursday.slice(0, 4)}-01-01`)) / 7) + 1;
  return `${year}-W${String(n).padStart(2, "0")}`;
}

export const isoWeek = (date: string) => weekLabel(weekIndex(date));

const WEEK_RE = /^(\d{4})-W(\d{2})$/;

/** The index of a week label; NaN when it is not one (or names a 53rd week the year does not have). */
export function weekIndexOf(label: string): number {
  const m = WEEK_RE.exec(label);
  if (!m) return NaN;
  const year = Number(m[1]);
  const n = Number(m[2]);
  // 4 January is always in week 1.
  const index = weekIndex(`${m[1]}-01-04`) + n - 1;
  return n >= 1 && n <= weeksInYear(year) ? index : NaN;
}

/** 52 or 53: the week of 28 December is always the last one. */
export const weeksInYear = (year: number) => Number(isoWeek(`${year}-12-28`).slice(6));

/** The month (`YYYY-MM`) a week belongs to: its Thursday's, like ISO does for years. */
export const weekMonth = (index: number) => weekThursday(index).slice(0, 7);

// ── Periods ────────────────────────────────────────────────────────────────────────────────────────

export type Period = "week" | "month" | "year";
export const PERIODS: readonly Period[] = ["week", "month", "year"];
export type PeriodRange = { period: Period; key: string; from: string; to: string };

/** The week, month or year holding `today` (the viewer's local date). */
export function periodRange(period: Period, today: string): PeriodRange {
  if (period === "week") {
    const i = weekIndex(today);
    return { period, key: weekLabel(i), from: weekMonday(i), to: dateOfDay(i * 7 + 3) };
  }
  if (period === "month") {
    const from = `${today.slice(0, 7)}-01`;
    const next = new Date(Date.UTC(Number(today.slice(0, 4)), Number(today.slice(5, 7)), 1)).toISOString().slice(0, 10);
    return { period, key: today.slice(0, 7), from, to: addDays(next, -1) };
  }
  return { period, key: today.slice(0, 4), from: `${today.slice(0, 4)}-01-01`, to: `${today.slice(0, 4)}-12-31` };
}

/** The weeks that belong to a period: those whose Thursday falls in it. */
export function periodWeeks(range: PeriodRange): number[] {
  if (range.period === "week") return [weekIndex(range.from)];
  const out: number[] = [];
  for (let i = weekIndex(range.from); weekThursday(i) <= range.to; i++) if (weekThursday(i) >= range.from) out.push(i);
  return out;
}

// ── What counts ────────────────────────────────────────────────────────────────────────────────────

/** The last day (UTC) an ascent dated `date` may be first stored and still count: GRACE_DAYS after its week's Sunday. */
export const countDeadline = (date: string) => dateOfDay(weekIndex(date) * 7 + 3 + GRACE_DAYS);

/** On time for boards and streaks. Not stored yet (`null`, waiting in the outbox) counts as stored now. */
export const onTime = (date: string, countedAt: string | null | undefined) => !countedAt || countedAt.slice(0, 10) <= countDeadline(date);

/** A board is final once nothing late can still enter it. Until then it is "Provisional". */
export const isFinal = (range: PeriodRange, todayUtc: string) => todayUtc > countDeadline(range.to);

export type PublishDelay = "next_day" | "now";

/**
 * The latest ascent date others may see at `now`: with "next_day" an ascent dated D shows from D + 1 at
 * PUBLISH_HOUR_UTC, when D has ended in every time zone; with "now" as soon as it is stored.
 */
export function publishedThrough(delay: PublishDelay, now: number): string {
  if (delay === "now") return "9999-12-31";
  return utcToday(now - (24 + PUBLISH_HOUR_UTC) * 3_600_000);
}

/** When an ascent dated `date` shows to others under "next_day". */
export const publishedAt = (date: string) => `${addDays(date, 1)}T${String(PUBLISH_HOUR_UTC).padStart(2, "0")}:00:00.000Z`;

export const cappedGain = (gain: number | null | undefined) => Math.min(gain ?? 0, GAIN_CAP_M);

export const isCustomPeak = (peakId: string) => peakId.startsWith("c-");

// ── A climber's ascents ────────────────────────────────────────────────────────────────────────────

export type StatAscent = {
  date: string;
  /** Ties on the same date go to the one logged first. */
  createdAt: string;
  /** When the server first stored it (or its new date); null while it waits in the outbox. */
  countedAt?: string | null;
  elevationGain: number | null;
  peakIds: readonly string[];
};

const byDate = (a: StatAscent, b: StatAscent) => a.date.localeCompare(b.date) || a.createdAt.localeCompare(b.createdAt);

/** Each peak's first ascent: the earliest date, then the one logged first. The stamp it earned. */
export function firstAscents<T extends StatAscent>(ascents: readonly T[]): Map<string, T> {
  const firsts = new Map<string, T>();
  for (const a of [...ascents].sort(byDate)) for (const id of a.peakIds) if (!firsts.has(id)) firsts.set(id, a);
  return firsts;
}

/** Weeks with at least one ascent on time, each with its earliest such date. */
export function activeWeeks(ascents: readonly StatAscent[]): Map<number, string> {
  const weeks = new Map<number, string>();
  for (const a of ascents) {
    if (!onTime(a.date, a.countedAt)) continue;
    const i = weekIndex(a.date);
    const cur = weeks.get(i);
    if (!cur || a.date < cur) weeks.set(i, a.date);
  }
  return weeks;
}

// ── Streak ("Racha de semanas") ────────────────────────────────────────────────────────────────────

export type Streak = {
  /** Active weeks in the run that reaches this week or the last one (this week never breaks it). */
  current: number;
  /** The longest run ever. */
  best: number;
  /** Refugio weeks banked. */
  refugio: number;
  /** The latest empty week a refugio covered in the current run ("Semana de refugio: el monte sigue ahí"). */
  refugioWeek: string | null;
  /** This week is paused. */
  paused: boolean;
};

export type StreakDetail = Streak & {
  /** When each streak logro's length was first reached (week index). */
  reached: Map<number, number>;
};

/**
 * Walks the weeks from the first active one to this one. An empty week holds the run when it is paused
 * or a refugio is banked (one is earned every REFUGIO_EVERY active weeks, at most REFUGIO_MAX kept, and
 * spent automatically); otherwise the run ends. Paused and refugio weeks add nothing to the count.
 */
export function computeStreak(active: Iterable<number>, paused: Iterable<number>, today: string): StreakDetail {
  const on = new Set(active);
  const held = new Set(paused);
  const now = weekIndex(today);
  const out: StreakDetail = { current: 0, best: 0, refugio: 0, refugioWeek: null, paused: held.has(now), reached: new Map() };
  const first = Math.min(...[...on].filter((i) => i <= now));
  if (!Number.isFinite(first)) return out;
  let since = 0;
  for (let i = first; i <= now; i++) {
    if (on.has(i)) {
      out.current++;
      if (++since === REFUGIO_EVERY) {
        out.refugio = Math.min(REFUGIO_MAX, out.refugio + 1);
        since = 0;
      }
      out.best = Math.max(out.best, out.current);
      for (const n of STREAK_LOGROS) if (out.current === n && !out.reached.has(n)) out.reached.set(n, i);
    } else if (i === now || held.has(i)) {
      continue;
    } else if (out.current > 0 && out.refugio > 0) {
      out.refugio--;
      out.refugioWeek = weekLabel(i);
    } else {
      out.current = 0;
      out.refugio = 0;
      out.refugioWeek = null;
      since = 0;
    }
  }
  return out;
}

export type PauseCheck = "ok" | "invalid" | "past" | "far" | "cap";

/** Whether `week` can be paused: from this week, within PAUSE_MAX_RUN weeks, PAUSE_MAX_PER_YEAR a year. */
export function pauseCheck(week: string, today: string, existing: readonly string[]): PauseCheck {
  const i = weekIndexOf(week);
  if (Number.isNaN(i)) return "invalid";
  if (existing.includes(week)) return "ok";
  const now = weekIndex(today);
  if (i < now) return "past";
  if (i >= now + PAUSE_MAX_RUN) return "far";
  return existing.filter((w) => w.slice(0, 4) === week.slice(0, 4)).length >= PAUSE_MAX_PER_YEAR ? "cap" : "ok";
}

// ── Logros ─────────────────────────────────────────────────────────────────────────────────────────

/** `first`, `tresmil`, `summits10`…, `list:<id>`, `streak4`…, `huellas10`. */
export type Logro = { id: string; date: string };

// ── Stats of a climber ─────────────────────────────────────────────────────────────────────────────

export type SocialStats = {
  peaks: number;
  ascents: number;
  gain: number;
  /** `months`: ascents per month, January first (rows stored before it was kept lack it until recomputed). */
  thisYear: { year: number; ascents: number; newPeaks: number; gain: number; months?: number[] };
  streak: Streak;
  logros: Logro[];
};

export type SocialStatsInput = {
  /** The ascents to count: the owner's own view passes all of them, others' views the visible ones. */
  ascents: readonly StatAscent[];
  /** Paused weeks (labels). */
  pauses?: readonly string[];
  today: string;
  elevation?: (peakId: string) => number | null | undefined;
  /** Curated lists, for "list completed". */
  lists?: readonly { id: string; peakIds: readonly string[] }[];
  /** Dates of the huellas received, for the huellas logro. */
  huellas?: readonly string[];
};

const LOGRO_ORDER = ["first", "tresmil", "summits", "list", "streak", "huellas"];
const logroRank = (id: string) => LOGRO_ORDER.findIndex((p) => id.startsWith(p));

export function computeSocialStats(input: SocialStatsInput): SocialStats {
  const { ascents, today } = input;
  const year = Number(today.slice(0, 4));
  const firsts = firstAscents(ascents);
  const thisYear = { year, ascents: 0, newPeaks: 0, gain: 0, months: Array.from({ length: 12 }, () => 0) };
  let gain = 0;
  for (const a of ascents) {
    gain += a.elevationGain ?? 0;
    if (Number(a.date.slice(0, 4)) === year) {
      thisYear.ascents++;
      thisYear.gain += a.elevationGain ?? 0;
      thisYear.months[Number(a.date.slice(5, 7)) - 1]!++;
    }
  }
  for (const a of firsts.values()) if (Number(a.date.slice(0, 4)) === year) thisYear.newPeaks++;

  const weeks = activeWeeks(ascents);
  const streak = computeStreak(weeks.keys(), (input.pauses ?? []).map(weekIndexOf), today);

  const logros: Logro[] = [];
  const summitDates = [...firsts.values()].map((a) => a.date).sort();
  if (summitDates.length) logros.push({ id: "first", date: summitDates[0]! });
  const tresmil = [...firsts].filter(([id]) => (input.elevation?.(id) ?? 0) >= TRESMIL_M).map(([, a]) => a.date).sort()[0];
  if (tresmil) logros.push({ id: "tresmil", date: tresmil });
  for (const n of SUMMIT_LOGROS) if (summitDates.length >= n) logros.push({ id: `summits${n}`, date: summitDates[n - 1]! });
  for (const l of input.lists ?? []) {
    const dates = l.peakIds.map((id) => firsts.get(id)?.date);
    if (dates.length && dates.every(Boolean)) logros.push({ id: `list:${l.id}`, date: (dates as string[]).sort().at(-1)! });
  }
  for (const [n, week] of streak.reached) logros.push({ id: `streak${n}`, date: weeks.get(week)! });
  const huellas = [...(input.huellas ?? [])].sort();
  if (huellas.length >= HUELLAS_LOGRO) logros.push({ id: `huellas${HUELLAS_LOGRO}`, date: huellas[HUELLAS_LOGRO - 1]! });
  logros.sort((a, b) => a.date.localeCompare(b.date) || logroRank(a.id) - logroRank(b.id));

  const { reached: _, ...plain } = streak;
  return { peaks: firsts.size, ascents: ascents.length, gain, thisYear, streak: plain, logros };
}

// ── Boards ─────────────────────────────────────────────────────────────────────────────────────────

export type BoardRow = { userId: string; name: string; value: number };
export type RankedRow = BoardRow & { place: number | null };

const byName = (a: BoardRow, b: BoardRow) => a.name.localeCompare(b.name, "es", { sensitivity: "base" }) || a.userId.localeCompare(b.userId);

/**
 * Highest first; ties share the place (1, 1, 3) and read alphabetically, with no tie-breaker. Rows at
 * zero go last, alphabetically and without a place: nobody is ever last.
 */
export function rankBoard(rows: readonly BoardRow[]): RankedRow[] {
  const scored = rows.filter((r) => r.value > 0).sort((a, b) => b.value - a.value || byName(a, b));
  const ranked = scored.map((r) => ({ ...r, place: 1 + scored.filter((o) => o.value > r.value).length }));
  return [...ranked, ...rows.filter((r) => r.value <= 0).sort(byName).map((r) => ({ ...r, place: null }))];
}

export type CircleSummit = { userId: string; peakId: string; firstDate: string; countedAt: string | null };
export type Huella = { leaderId: string; followerId: string; peakId: string; date: string };

/**
 * "M ha seguido tu huella": for every new summit of member M in [from, to], the other members whose first
 * ascent of that peak is the earliest, on a strictly earlier date, get one huella each. The same date
 * means they climbed it together: nobody gets it. Custom peaks (nobody else can have them) and late
 * ascents never count.
 */
export function huellas(summits: readonly CircleSummit[], from: string, to: string): Huella[] {
  const byPeak = new Map<string, CircleSummit[]>();
  for (const s of summits) {
    if (isCustomPeak(s.peakId) || !onTime(s.firstDate, s.countedAt)) continue;
    const list = byPeak.get(s.peakId);
    if (list) list.push(s);
    else byPeak.set(s.peakId, [s]);
  }
  const out: Huella[] = [];
  for (const [peakId, list] of byPeak) {
    for (const m of list) {
      if (m.firstDate < from || m.firstDate > to) continue;
      const earlier = list.filter((o) => o.userId !== m.userId && o.firstDate < m.firstDate);
      if (!earlier.length) continue;
      const first = earlier.reduce((d, o) => (o.firstDate < d ? o.firstDate : d), earlier[0]!.firstDate);
      for (const o of earlier) if (o.firstDate === first) out.push({ leaderId: o.userId, followerId: m.userId, peakId, date: m.firstDate });
    }
  }
  return out;
}

/** The shared goal: the circle's gain as Everests, the bar to the next whole one, and its new summits. */
export function cordada(gain: number, newSummits: number) {
  const everests = gain / EVEREST_M;
  return { gain, everests, progress: everests - Math.floor(everests), newSummits };
}
